/**
 * Reportes → Kardex del producto (`/reportes/kardex`).
 *
 * Puerto de las dos pantallas de kardex del panel anterior:
 * *Kardex Individual* (`Kardex::index` + `Kardex::report` + `Kardex_model`) y
 * *Kardex Valorizado* (`Inventory::kardex_valorizado` + `API/Products::kardex_valorizado`
 * + `API/Inventory_model`). Las dos son lo mismo —los movimientos de un producto de
 * surtidor en un rango, con su saldo acumulado— y sólo cambian la fuente de las
 * entradas (almacén o compras, que traen el costo) y las columnas de valor, así que
 * aquí son **una pantalla con dos vistas** (`?vista=valorizado`).
 *
 * Reglas conservadas:
 * - El producto es el de **surtidor** (`pts_fuel_grades`), que guarda el `Product`
 *   de inventario con el que se cruzan compras, salidas y ajustes.
 * - El **saldo inicial** se calcula como en el original: entradas − salidas − ventas
 *   + ajustes de todo lo anterior al rango, y cada línea arrastra el saldo acumulado.
 * - Las ventas salen del surtidor (`pts_pump_transactions`) del producto y las
 *   entradas, de `detalle_entrada` en la vista individual y de `compra_producto` en
 *   la valorizada; los ajustes suman o restan según su tipo (`entrada`/`salida`).
 *
 * Diferencias anotadas:
 * - Las transacciones **anuladas** (`voidedTransaction = 1`) y las de **calibración**
 *   no se cuentan: no son ventas. El original sólo excluía las calibraciones y sólo
 *   al calcular el saldo inicial.
 * - Las ventas se clasifican por `FuelGradeId` (el dato real de la transacción) en
 *   vez del mapa de boquillas `pts_nozzles` que usaba el original: en esta copia ese
 *   mapa apunta a otros productos (la boquilla 1 del surtidor 1 despacha DIESEL
 *   mientras el mapa dice REGULAR 1).
 * - El CSV y el PDF los sirve la API y el `ajuste_inventario` se lee, nunca se
 *   escribe (el original guardaba un stock de cierre al abrir el kardex).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateTimeValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosKardex,
  useKardex,
  type FiltrosKardex,
} from '../reportes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Las vistas del kardex (las dos pantallas del panel anterior). */
const VISTAS: { valor: FiltrosKardex['vista']; etiqueta: string; detalle: string }[] = [
  { valor: 'individual', etiqueta: 'Individual', detalle: 'movimientos de almacén' },
  { valor: 'valorizado', etiqueta: 'Valorizado', detalle: 'compras con su costo' },
];

/** Los colores del tipo de movimiento. */
const TIPOS: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  COMPRA: 'success',
  ENTRADA: 'success',
  VENTA: 'danger',
  SALIDA: 'warning',
  AJUSTE: 'neutral',
};

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: el mes en curso. */
function rangoPorDefecto(vista: FiltrosKardex['vista']): FiltrosKardex {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(hoy),
    producto: '',
    vista,
  };
}

export function ReporteKardexPage() {
  useDocumentTitle('Reportes · Kardex del producto');

  const [parametros] = useSearchParams();
  const vistaDeLaUrl: FiltrosKardex['vista'] =
    parametros.get('vista') === 'valorizado' ? 'valorizado' : 'individual';

  const [filtros, setFiltros] = useState<FiltrosKardex>(() => rangoPorDefecto(vistaDeLaUrl));
  const [aviso, setAviso] = useState<string | null>(null);
  const [ajustado, setAjustado] = useState(false);

  const catalogos = useCatalogosKardex();
  const productos = catalogos.data?.productos ?? [];
  const ultimoDiaConDatos = catalogos.data?.rango.hasta ?? null;

  // El mes en curso puede estar vacío (la copia puede terminar antes), así que el
  // rango por defecto se ajusta una vez a los últimos 30 días **con datos**.
  useEffect(() => {
    if (ajustado || ultimoDiaConDatos === null) return;

    const fin = new Date(`${ultimoDiaConDatos}T12:00:00`);
    const inicio = new Date(fin.getTime() - 29 * 24 * 60 * 60 * 1000);

    setFiltros((previos) => ({ ...previos, desde: fechaLocal(inicio), hasta: fechaLocal(fin) }));
    setAjustado(true);
  }, [ajustado, ultimoDiaConDatos]);

  // El producto del kardex se elige de los productos de surtidor.
  const productoElegido = filtros.producto || String(productos[0]?.id ?? '');
  const consulta = useKardex({ ...filtros, producto: productoElegido });
  const datos = consulta.data;
  const resumen = datos?.resumen;
  const filas = datos?.filas ?? [];
  // El kardex de un mes puede traer cientos de movimientos: se pagina en el navegador y el
  // saldo del pie sigue saliendo del listado completo.
  const paginacion = usePaginacion(filas, { clave: filtros });

  const cambiar = <C extends keyof FiltrosKardex>(campo: C, valor: FiltrosKardex[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarKardex({ ...filtros, producto: productoElegido }), {
        nombre: `kardex-${productoElegido}-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el kardex.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfKardex({ ...filtros, producto: productoElegido }));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const irA = (rango: { desde: string; hasta: string }) =>
    setFiltros((previos) => ({ ...previos, ...rango }));

  const hoy = new Date();
  const esMesActual =
    filtros.desde === fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)) &&
    filtros.hasta === fechaLocal(hoy);
  const negativo = (resumen?.saldo_final ?? 0) < 0;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">
            Kardex {filtros.vista === 'valorizado' ? 'valorizado' : 'individual'}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Entradas, ventas de surtidor, salidas y ajustes del producto con su saldo acumulado.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={productoElegido}
                onChange={({ target }) => cambiar('producto', target.value)}
                className={CONTROL}
                aria-label="Producto"
              >
                {productos.map((producto) => (
                  <option key={producto.id} value={String(producto.id)}>
                    {producto.nombre} · {formatCurrency(producto.precio)} / u. (
                    {formatNumber(producto.transacciones)} transacciones)
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-ink-100 pt-3">
            <div className="inline-flex overflow-hidden rounded-control border border-ink-200">
              {VISTAS.map((opcion) => (
                <button
                  key={opcion.valor}
                  type="button"
                  aria-pressed={filtros.vista === opcion.valor}
                  title={opcion.detalle}
                  onClick={() => cambiar('vista', opcion.valor)}
                  className={cn(
                    'px-3 py-2 text-xs font-semibold transition-colors',
                    filtros.vista === opcion.valor
                      ? 'bg-brand-50 text-brand-700'
                      : 'bg-white text-ink-600 hover:text-brand-700',
                  )}
                >
                  {opcion.etiqueta}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                aria-pressed={esMesActual}
                onClick={() =>
                  irA({
                    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
                    hasta: fechaLocal(hoy),
                  })
                }
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                  esMesActual
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                )}
              >
                Mes actual
              </button>

              <span className="text-xs text-ink-500">
                {catalogos.data?.rango.desde
                  ? `la copia tiene movimientos entre ${catalogos.data.rango.desde} y ${catalogos.data.rango.hasta}`
                  : ''}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el kardex">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {[
          {
            etiqueta: 'Saldo inicial',
            valor: formatNumber(resumen?.saldo_inicial ?? 0),
            detalle: 'antes del rango',
          },
          {
            etiqueta: 'Entradas',
            valor: formatNumber(resumen?.entradas ?? 0),
            detalle: formatCurrency(resumen?.entradas_importe ?? 0),
          },
          {
            etiqueta: 'Ventas',
            valor: formatNumber(resumen?.ventas ?? 0),
            detalle: formatCurrency(resumen?.ventas_importe ?? 0),
          },
          {
            etiqueta: 'Salidas',
            valor: formatNumber(resumen?.salidas ?? 0),
            detalle: 'de almacén',
          },
          {
            etiqueta: 'Ajustes',
            valor: formatNumber(resumen?.ajustes ?? 0),
            detalle: 'de inventario',
          },
          {
            etiqueta: 'Saldo final',
            valor: formatNumber(resumen?.saldo_final ?? 0),
            detalle:
              resumen?.valor_final === null || resumen?.valor_final === undefined
                ? `${formatNumber(resumen?.movimientos ?? 0)} movimiento(s)`
                : formatCurrency(resumen.valor_final),
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p
                className={cn(
                  'text-lg font-bold',
                  tarjeta.etiqueta === 'Saldo final' && negativo ? 'text-rose-700' : 'text-ink-900',
                )}
              >
                {tarjeta.valor}
              </p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {negativo && !consulta.isLoading ? (
        <Alert variant="warning" title="El saldo del producto es negativo">
          Las compras y entradas registradas de este producto no cubren las ventas del surtidor del
          periodo (el saldo arrastra lo anterior al rango). Suele significar que falta registrar
          compras, no que falte combustible: revisa las entradas de almacén y los ajustes de
          inventario.
        </Alert>
      ) : null}

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Movimientos ({formatNumber(resumen?.movimientos ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {datos?.producto.nombre ?? ''} · {formatNumber(resumen?.dias ?? 0)} día(s) con
              movimiento · {filtros.vista === 'valorizado' ? 'entradas por compra' : 'entradas de almacén'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[49rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[8.5rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Tipo</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Comprobante</th>
                    <th className="px-1.5 py-2">Concepto</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Volumen</th>
                    <th className="w-[5.25rem] px-1.5 py-2 text-right">Precio</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Importe</th>
                    <th className="w-[6rem] px-1.5 py-2 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay movimientos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  <tr className="border-t border-ink-100 bg-ink-50/60">
                    <td className="px-1.5 py-2 text-[11px] font-semibold tracking-wide text-ink-500 uppercase" colSpan={7}>
                      Saldo inicial del rango
                    </td>
                    <td className="px-1.5 py-2 text-right font-semibold text-ink-800">
                      {formatNumber(resumen?.saldo_inicial ?? 0)}
                    </td>
                  </tr>

                  {paginacion.visibles.map((fila, indice) => (
                    <tr
                      key={`${fila.fecha}-${fila.tipo}-${fila.comprobante}-${indice}`}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2 text-ink-700">{formatDateTimeValue(fila.fecha)}</td>
                      <td className="px-1.5 py-2">
                        <Badge variant={TIPOS[fila.tipo] ?? 'neutral'} className="px-1.5 py-0 text-[10px]">
                          {fila.tipo}
                        </Badge>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-700" title={fila.comprobante ?? ''}>
                          {fila.comprobante || '—'}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-600" title={fila.concepto ?? ''}>
                          {fila.concepto || '—'}
                        </p>
                      </td>
                      <td
                        className={cn(
                          'px-1.5 py-2 text-right font-semibold',
                          fila.signo === 1 ? 'text-emerald-700' : 'text-ink-800',
                        )}
                      >
                        {fila.signo === 1 ? '+' : '−'}
                        {formatNumber(fila.volumen)}
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-600">
                        {fila.precio_unitario === null ? '—' : formatCurrency(fila.precio_unitario)}
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {fila.importe === null ? '—' : formatCurrency(fila.importe)}
                      </td>
                      <td
                        className={cn(
                          'px-1.5 py-2 text-right font-semibold',
                          fila.saldo < 0 ? 'text-rose-700' : 'text-ink-900',
                        )}
                      >
                        {formatNumber(fila.saldo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        SUMA · {formatNumber(resumen?.movimientos ?? 0)} movimiento(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(
                          (resumen?.entradas ?? 0) -
                            (resumen?.ventas ?? 0) -
                            (resumen?.salidas ?? 0) +
                            (resumen?.ajustes ?? 0),
                        )}
                      </td>
                      <td className="px-1.5 py-2 text-right text-[11px] font-normal text-ink-500">
                        {resumen?.costo_promedio === null || resumen?.costo_promedio === undefined
                          ? 'sin entradas'
                          : `costo ${formatCurrency(resumen.costo_promedio)}`}
                      </td>
                      <td className="px-1.5 py-2 text-right">{formatCurrency(resumen?.entradas_importe ?? 0)}</td>
                      <td className="px-1.5 py-2 text-right">{formatNumber(resumen?.saldo_final ?? 0)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="movimientos" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          El <strong>saldo inicial</strong> es todo lo anterior al rango (entradas − salidas − ventas
          + ajustes) y cada línea arrastra el saldo; las ventas salen de las transacciones del
          surtidor del producto (sin las anuladas ni las de calibración) y las entradas, del almacén
          en la vista <em>individual</em> o de las compras —con su costo— en la <em>valorizada</em>.
        </span>
      </p>
    </div>
  );
}
